import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  Add,
  Delete,
  Edit,
  UploadFile,
  PushPin,
  Refresh,
} from "@mui/icons-material";
import { apiService, API_CONFIG, API_ENDPOINTS } from "../../config/api";
import type { DashboardUpdate } from "../../types";

interface LatestUpdatesCardProps {
  editable?: boolean;
  title?: string;
  maxItems?: number;
  variant?: "card" | "compact";
  sx?: SxProps<Theme>;
}

const UPDATE_TITLE_MAX_LENGTH = 48;

type UpdateFormState = {
  title: string;
  body: string;
  imageUrl: string;
  imageName: string;
  pinned: boolean;
};

const emptyForm: UpdateFormState = {
  title: "",
  body: "",
  imageUrl: "",
  imageName: "",
  pinned: false,
};

function formatDateOnly(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const LatestUpdatesCard: React.FC<LatestUpdatesCardProps> = ({
  editable = false,
  title = "Latest Updates",
  maxItems,
  variant = "card",
  sx,
}) => {
  const [updates, setUpdates] = useState<DashboardUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState<DashboardUpdate | null>(null);
  const [formState, setFormState] = useState<UpdateFormState>(emptyForm);

  const visibleUpdates = useMemo(() => {
    if (!maxItems) return updates;
    return updates.slice(0, maxItems);
  }, [maxItems, updates]);

  const latestUpdate = updates[0] || null;

  const loadUpdates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const suffix = editable ? "?includeAll=true" : "";
      const response = await apiService.get(`${API_ENDPOINTS.DASHBOARD_UPDATES.LIST}${suffix}`);
      setUpdates(response.items || []);
    } catch (err: any) {
      setError(err.message || "Failed to load dashboard updates");
    } finally {
      setLoading(false);
    }
  }, [editable]);

  useEffect(() => {
    loadUpdates();
  }, [loadUpdates]);

  const openCreateDialog = () => {
    setEditingUpdate(null);
    setFormState(emptyForm);
    setViewerOpen(true);
    setDialogOpen(true);
  };

  const openEditDialog = (update: DashboardUpdate) => {
    setEditingUpdate(update);
    setFormState({
      title: update.title || "",
      body: update.body || "",
      imageUrl: update.imageUrl || "",
      imageName: update.imageUrl ? "Current image" : "",
      pinned: Boolean(update.pinned),
    });
    setViewerOpen(true);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingUpdate(null);
    setFormState(emptyForm);
  };

  const handleSave = async () => {
    if (!formState.title.trim() || !formState.body.trim()) {
      setError("Title and body are required");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload = {
        title: formState.title.trim(),
        body: formState.body.trim(),
        imageUrl: formState.imageUrl.trim() || null,
        pinned: formState.pinned,
        isPublished: true,
      };

      if (editingUpdate) {
        await apiService.patch(API_ENDPOINTS.DASHBOARD_UPDATES.DETAIL(editingUpdate.id), payload);
      } else {
        await apiService.post(API_ENDPOINTS.DASHBOARD_UPDATES.LIST, payload);
      }

      closeDialog();
      await loadUpdates();
    } catch (err: any) {
      setError(err.message || "Failed to save update");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file: File | null) => {
    if (!file) return;

    try {
      setSaving(true);
      setError(null);
      const token = await apiService.getAuthToken();
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_ENDPOINTS.DASHBOARD_UPDATES.UPLOAD_IMAGE}`,
        {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to upload image");
      }

      const payload = await response.json();
      setFormState((prev) => ({
        ...prev,
        imageUrl: payload.imageUrl || "",
        imageName: payload.originalName || file.name,
      }));
    } catch (err: any) {
      setError(err.message || "Failed to upload image");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (updateId: string) => {
    try {
      setError(null);
      await apiService.delete(API_ENDPOINTS.DASHBOARD_UPDATES.DETAIL(updateId));
      await loadUpdates();
    } catch (err: any) {
      setError(err.message || "Failed to delete update");
    }
  };

  const renderUpdateList = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (visibleUpdates.length === 0) {
      return <Alert severity="info">No updates yet.</Alert>;
    }

    return (
      <Stack spacing={2}>
        {visibleUpdates.map((update) => (
          <Card
            key={update.id}
            variant="outlined"
            sx={{ borderRadius: 2, overflow: "hidden" }}
          >
            {update.imageUrl && (
              <CardMedia
                component="img"
                image={update.imageUrl}
                alt={update.title}
                sx={{ maxHeight: 220, objectFit: "cover" }}
              />
            )}
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {update.title}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                    {update.pinned && (
                      <Chip size="small" icon={<PushPin />} label="Pinned" color="warning" />
                    )}
                    {editable && (
                      <Chip
                        size="small"
                        label={update.isPublished ? "Published" : "Draft"}
                        color={update.isPublished ? "success" : "default"}
                      />
                    )}
                  </Stack>
                </Box>
                {editable && (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit update">
                      <IconButton size="small" onClick={() => openEditDialog(update)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete update">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(update.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {update.body}
              </Typography>

              {formatDateOnly(update.updatedAt || update.createdAt) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                  Updated {formatDateOnly(update.updatedAt || update.createdAt)}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  const compactTrigger = (
    <Card
      elevation={2}
      sx={{
        width: { xs: "100%", md: 460 },
        maxWidth: "100%",
        borderRadius: 3,
        cursor: "pointer",
        border: "1px solid",
        borderColor: "divider",
        ...sx,
      }}
      onClick={() => setViewerOpen(true)}
    >
      <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1 }}>
              {title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "flex-start" }}>
            <Tooltip title="Refresh updates">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  loadUpdates();
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            {editable && (
              <Tooltip title="Add update">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    openCreateDialog();
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}

        {!loading && latestUpdate && (
          <Box
            sx={{
              mt: 1.25,
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              backgroundColor: "grey.50",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                }}
              >
                {latestUpdate.title}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <>
      {variant === "compact" ? compactTrigger : (
      <Card elevation={2} sx={{ height: "100%" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh updates">
                <IconButton size="small" onClick={loadUpdates}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
              {editable && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openCreateDialog}
                >
                  Add
                </Button>
              )}
            </Stack>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {renderUpdateList()}
        </CardContent>
      </Card>
      )}

      <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 10 }}>
          {title}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Published product and study platform updates in one place.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {renderUpdateList()}
        </DialogContent>
        <DialogActions>
          <Button onClick={loadUpdates} startIcon={<Refresh />}>
            Refresh
          </Button>
          {editable && (
            <Button onClick={openCreateDialog} variant="outlined" startIcon={<Add />}>
              Add Update
            </Button>
          )}
          <Button onClick={() => setViewerOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {editable && (
        <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>
            {editingUpdate ? "Edit Update" : "Add Update"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Title"
                value={formState.title}
                onChange={(event) =>
                  setFormState((prev) => ({
                    ...prev,
                    title: event.target.value.slice(0, UPDATE_TITLE_MAX_LENGTH),
                  }))
                }
                fullWidth
                required
                inputProps={{ maxLength: UPDATE_TITLE_MAX_LENGTH }}
                helperText={`Keep it concise: ${formState.title.length}/${UPDATE_TITLE_MAX_LENGTH} characters`}
              />
              <TextField
                label="Message"
                value={formState.body}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, body: event.target.value }))
                }
                fullWidth
                required
                multiline
                minRows={4}
              />
              <Stack spacing={1}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFile />}
                  disabled={saving}
                >
                  {formState.imageUrl ? "Replace image" : "Upload image"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      void handleImageUpload(file);
                      event.target.value = "";
                    }}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Optional. Upload an image to appear with the update card.
                </Typography>
                {formState.imageName && (
                  <Typography variant="body2" color="text.secondary">
                    Selected image: {formState.imageName}
                  </Typography>
                )}
                {formState.imageUrl && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() =>
                      setFormState((prev) => ({ ...prev, imageUrl: "", imageName: "" }))
                    }
                    sx={{ alignSelf: "flex-start", px: 0 }}
                  >
                    Remove image
                  </Button>
                )}
              </Stack>
              {formState.imageUrl && (
                <Box
                  component="img"
                  src={formState.imageUrl}
                  alt="Update preview"
                  sx={{
                    width: "100%",
                    maxHeight: 220,
                    objectFit: "cover",
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.pinned}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, pinned: event.target.checked }))
                    }
                  />
                }
                label="Pin this update so it stays at the top of the list"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Pinned updates stay at the top for everyone who can see dashboard updates.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {editingUpdate ? "Save Changes" : "Create Update"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default LatestUpdatesCard;
